import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const VoiceContext = createContext();

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
};

export const VoiceProvider = ({ children }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [recognition, setRecognition] = useState(null);
  const [language, setLanguage] = useState('es-ES'); 
  const [version, setVersion] = useState(0); // For hard reset

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setIsSupported(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const newRecognition = new SpeechRecognition();
    
    newRecognition.continuous = true;
    newRecognition.interimResults = true;
    newRecognition.lang = language;
    newRecognition.maxAlternatives = 1;

    newRecognition.onstart = () => {
      setIsListening(true);
      setTranscript('');
      setInterimTranscript('');
    };

    newRecognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        setTranscript(prev => prev + finalTranscript);
      }
      setInterimTranscript(interimTranscript);
    };

    newRecognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        setIsSupported(false);
      } else if (event.error === 'network') {
        console.warn('Network error in Speech Recognition. Retrying...');
        // Transient network error mitigation
        if (isListening) {
          setTimeout(() => {
            try { newRecognition.start(); } catch(e) {}
          }, 1000);
        }
      }
      // Don't flip isListening to false immediately on network errors if we want to retry
      if (event.error !== 'network') setIsListening(false);
    };

    newRecognition.onend = () => {
      setIsListening(false);
    };

    setRecognition(newRecognition);

    return () => {
      newRecognition.onstart = null;
      newRecognition.onresult = null;
      newRecognition.onerror = null;
      newRecognition.onend = null;
      try { newRecognition.stop(); } catch(e) {}
    };
  }, [language, version]);

  const reinitialize = useCallback(() => {
    setIsListening(false);
    setVersion(v => v + 1);
  }, []);

  const startListening = useCallback(() => {
    if (recognition && !isListening) {
      try {
        recognition.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
      }
    }
  }, [recognition, isListening]);

  const stopListening = useCallback(() => {
    if (recognition && isListening) {
      recognition.stop();
    }
  }, [recognition, isListening]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
  }, []);

  const speak = useCallback((text, lang = language) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      window.speechSynthesis.speak(utterance);
    }
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    const langMap = {
      'eu': 'eu-ES',
      'es': 'es-ES',
      'en': 'en-US'
    };
    
    const newLang = langMap[lang] || lang;
    setLanguage(newLang);
    
    if (recognition) {
      recognition.lang = newLang;
    }
  }, [recognition]);

  const value = {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    language,
    startListening,
    stopListening,
    resetTranscript,
    speak,
    changeLanguage,
    reinitialize,
    fullTranscript: transcript + interimTranscript
  };

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  );
};
